import { useCallback, useState, useRef, useEffect } from 'react';
import type { Editor } from '@tiptap/react';

export interface FormattingToolbarProps {
  editor: Editor | null;
}

/**
 * Color palette with 12+ colors for text formatting.
 */
const COLOR_PALETTE = [
  { name: 'Black', value: '#000000' },
  { name: 'Dark Gray', value: '#4A4A4A' },
  { name: 'Red', value: '#DC2626' },
  { name: 'Orange', value: '#EA580C' },
  { name: 'Yellow', value: '#CA8A04' },
  { name: 'Green', value: '#16A34A' },
  { name: 'Blue', value: '#2563EB' },
  { name: 'Purple', value: '#9333EA' },
  { name: 'Pink', value: '#DB2777' },
  { name: 'Brown', value: '#92400E' },
  { name: 'Teal', value: '#0D9488' },
  { name: 'Magenta', value: '#C026D3' },
];

/**
 * FormattingToolbar provides rich text formatting controls for the TipTap editor.
 * Includes: bold, italic, underline, strikethrough, text color (12+ colors),
 * headings (H1-H3), bullet/ordered/task lists, and horizontal divider.
 */
export function FormattingToolbar({ editor }: FormattingToolbarProps) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);

  // Close color picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleColorPicker = useCallback(() => {
    setShowColorPicker(prev => !prev);
  }, []);

  const applyColor = useCallback((color: string) => {
    if (!editor) return;
    editor.chain().focus().setColor(color).run();
    setShowColorPicker(false);
  }, [editor]);

  if (!editor) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-0.5 p-2 border-b border-border bg-bg-secondary rounded-t-sm"
      role="toolbar"
      aria-label="Formatting toolbar"
    >
      {/* Text formatting group */}
      <div className="flex items-center gap-0.5" role="group" aria-label="Text formatting">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
          aria-label="Bold"
        >
          <span className="font-bold">B</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
          aria-label="Italic"
        >
          <span className="italic">I</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline"
          aria-label="Underline"
        >
          <span className="underline">U</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Strikethrough"
          aria-label="Strikethrough"
        >
          <span className="line-through">S</span>
        </ToolbarButton>
      </div>

      <ToolbarDivider />

      {/* Text color */}
      <div className="relative" ref={colorPickerRef}>
        <ToolbarButton
          onClick={toggleColorPicker}
          isActive={showColorPicker}
          title="Text Color"
          aria-label="Text Color"
          aria-expanded={showColorPicker}
          aria-haspopup="true"
        >
          <span className="flex flex-col items-center">
            <span className="text-xs font-semibold leading-none">A</span>
            <span
              className="w-4 h-1 mt-0.5 rounded-full"
              style={{ backgroundColor: editor.getAttributes('textStyle').color || '#000000' }}
            />
          </span>
        </ToolbarButton>

        {showColorPicker && (
          <div
            className="absolute top-full left-0 mt-1 p-2 bg-bg-card rounded-sm shadow-hover border border-border z-50 grid grid-cols-4 gap-1"
            role="listbox"
            aria-label="Color palette"
          >
            {COLOR_PALETTE.map(color => (
              <button
                key={color.value}
                onClick={() => applyColor(color.value)}
                className="w-6 h-6 rounded-full border border-border hover:scale-110 transition-transform duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                style={{ backgroundColor: color.value }}
                title={color.name}
                aria-label={color.name}
                role="option"
                aria-selected={editor.getAttributes('textStyle').color === color.value}
              />
            ))}
          </div>
        )}
      </div>

      <ToolbarDivider />

      {/* Heading group */}
      <div className="flex items-center gap-0.5" role="group" aria-label="Headings">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
          aria-label="Heading 1"
        >
          <span className="text-xs font-bold">H1</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
          aria-label="Heading 2"
        >
          <span className="text-xs font-bold">H2</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
          aria-label="Heading 3"
        >
          <span className="text-xs font-bold">H3</span>
        </ToolbarButton>
      </div>

      <ToolbarDivider />

      {/* List group */}
      <div className="flex items-center gap-0.5" role="group" aria-label="Lists">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
          aria-label="Bullet List"
        >
          <BulletListIcon />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
          aria-label="Numbered List"
        >
          <OrderedListIcon />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          isActive={editor.isActive('taskList')}
          title="Checklist"
          aria-label="Checklist"
        >
          <TaskListIcon />
        </ToolbarButton>
      </div>

      <ToolbarDivider />

      {/* Horizontal rule */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        isActive={false}
        title="Horizontal Divider"
        aria-label="Horizontal Divider"
      >
        <HorizontalRuleIcon />
      </ToolbarButton>
    </div>
  );
}

/** Toolbar button wrapper with active state styling */
interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive: boolean;
  children: React.ReactNode;
}

function ToolbarButton({ isActive, children, className = '', ...props }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className={[
        'inline-flex items-center justify-center w-8 h-8 rounded-sm text-sm',
        'transition-all duration-fast',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
        isActive
          ? 'bg-primary-light text-primary-dark'
          : 'text-text-secondary hover:bg-hover-bg hover:text-text-primary',
        className,
      ].join(' ')}
      {...props}
    >
      {children}
    </button>
  );
}

/** Vertical divider between toolbar groups */
function ToolbarDivider() {
  return <div className="w-px h-5 bg-border mx-1" aria-hidden="true" />;
}

/* --- Icons --- */

function BulletListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <circle cx="4" cy="6" r="1" fill="currentColor" />
      <circle cx="4" cy="12" r="1" fill="currentColor" />
      <circle cx="4" cy="18" r="1" fill="currentColor" />
    </svg>
  );
}

function OrderedListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="10" y1="6" x2="21" y2="6" />
      <line x1="10" y1="12" x2="21" y2="12" />
      <line x1="10" y1="18" x2="21" y2="18" />
      <text x="3" y="8" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">1</text>
      <text x="3" y="14" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">2</text>
      <text x="3" y="20" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">3</text>
    </svg>
  );
}

function TaskListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="4" height="4" rx="1" />
      <line x1="10" y1="6" x2="21" y2="6" />
      <rect x="3" y="10" width="4" height="4" rx="1" />
      <line x1="10" y1="12" x2="21" y2="12" />
      <path d="M4 17l1.5 1.5L8 16" />
      <line x1="10" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function HorizontalRuleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="3" y1="12" x2="21" y2="12" />
    </svg>
  );
}

export default FormattingToolbar;
